// packages/shared/src/utils/crypto/sensitiveEntryCrypto.js

const SENSITIVE_ENTRY_ALGO = 'aes-256-gcm';
const SENSITIVE_ENTRY_SCHEME = 'vault-aes-256-gcm-v1';
const SENSITIVE_ENTRY_KEY_VERSION = 1;

const AES_GCM_IV_LENGTH = 12;
const AES_GCM_TAG_LENGTH = 16;
const AES_KEY_LENGTH_BYTES = 32;

export function buildSensitiveEntryAadPreview({
  userId,
  entryId,
  fieldName,
  keyVersion = 2,
}) {
  return {
    scope: 'sensitive-entry',
    userId: String(userId ?? ''),
    entryId: String(entryId ?? ''),
    fieldName,
    keyVersion,
  };
}

function getWebCrypto() {
  const cryptoObj = globalThis?.crypto;

  if (!cryptoObj?.subtle || typeof cryptoObj.getRandomValues !== 'function') {
    throw new Error(
      'Web Crypto API is not available in this environment for sensitive entry encryption'
    );
  }

  return cryptoObj;
}

function utf8Encode(value) {
  return new TextEncoder().encode(value ?? '');
}

function utf8Decode(bytes) {
  return new TextDecoder().decode(bytes);
}

function arrayBufferToUint8Array(buffer) {
  return buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
}

function uint8ArrayToBase64(bytes) {
  let binary = '';
  const chunkSize = 0x8000;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  return btoa(binary);
}

function base64ToUint8Array(base64) {
  if (!base64 || typeof base64 !== 'string') {
    throw new Error('Expected base64 string');
  }

  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
}

function splitCiphertextAndAuthTag(encryptedBytes) {
  if (!(encryptedBytes instanceof Uint8Array)) {
    throw new Error('Encrypted payload must be a Uint8Array');
  }

  if (encryptedBytes.length < AES_GCM_TAG_LENGTH) {
    throw new Error('Encrypted payload is too short to contain auth tag');
  }

  const ciphertext = encryptedBytes.slice(
    0,
    encryptedBytes.length - AES_GCM_TAG_LENGTH
  );
  const authTag = encryptedBytes.slice(
    encryptedBytes.length - AES_GCM_TAG_LENGTH
  );

  return { ciphertext, authTag };
}

function combineCiphertextAndAuthTag(ciphertextBytes, authTagBytes) {
  if (
    !(ciphertextBytes instanceof Uint8Array) ||
    !(authTagBytes instanceof Uint8Array)
  ) {
    throw new Error('Ciphertext and auth tag must be Uint8Array values');
  }

  const combined = new Uint8Array(ciphertextBytes.length + authTagBytes.length);
  combined.set(ciphertextBytes, 0);
  combined.set(authTagBytes, ciphertextBytes.length);

  return combined;
}

function buildAadBytes(aad) {
  if (!aad) return undefined;

  if (typeof aad === 'string') {
    return utf8Encode(aad);
  }

  return utf8Encode(JSON.stringify(aad));
}

async function importSensitiveEntryKey(rawKey) {
  const cryptoObj = getWebCrypto();

  let keyBytes;

  if (rawKey instanceof Uint8Array) {
    keyBytes = rawKey;
  } else if (rawKey instanceof ArrayBuffer) {
    keyBytes = new Uint8Array(rawKey);
  } else if (typeof rawKey === 'string') {
    keyBytes = base64ToUint8Array(rawKey);
  } else {
    throw new Error(
      'Sensitive entry key must be a CryptoKey, Uint8Array, ArrayBuffer, or base64 string'
    );
  }

  if (keyBytes.length !== AES_KEY_LENGTH_BYTES) {
    throw new Error(
      `Sensitive entry key must be ${AES_KEY_LENGTH_BYTES} bytes for AES-256-GCM`
    );
  }

  return cryptoObj.subtle.importKey(
    'raw',
    keyBytes,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

async function normalizeSensitiveEntryKey(key) {
  if (!key) {
    throw new Error('Sensitive entry key is required');
  }

  if (typeof CryptoKey !== 'undefined' && key instanceof CryptoKey) {
    return key;
  }

  return importSensitiveEntryKey(key);
}

function randomIv() {
  const cryptoObj = getWebCrypto();
  const iv = new Uint8Array(AES_GCM_IV_LENGTH);
  cryptoObj.getRandomValues(iv);
  return iv;
}

export async function generateSensitiveEntryKey() {
  const cryptoObj = getWebCrypto();

  return cryptoObj.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, [
    'encrypt',
    'decrypt',
  ]);
}

export async function exportSensitiveEntryKeyToBase64(key) {
  const cryptoObj = getWebCrypto();
  const normalizedKey = await normalizeSensitiveEntryKey(key);
  const raw = await cryptoObj.subtle.exportKey('raw', normalizedKey);
  return uint8ArrayToBase64(arrayBufferToUint8Array(raw));
}

export async function encryptSensitiveField({
  plaintext,
  key,
  fieldName = 'detail',
  keyVersion = SENSITIVE_ENTRY_KEY_VERSION,
  aad = null,
}) {
  const cryptoObj = getWebCrypto();
  const cryptoKey = await normalizeSensitiveEntryKey(key);

  const iv = randomIv();
  const plaintextBytes = utf8Encode(plaintext ?? '');

  const aadValue =
    aad ??
    JSON.stringify({
      scope: 'sensitive-entry',
      fieldName,
      keyVersion,
    });

  const additionalData = buildAadBytes(aadValue);

  const encryptedBuffer = await cryptoObj.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
      tagLength: AES_GCM_TAG_LENGTH * 8,
      additionalData,
    },
    cryptoKey,
    plaintextBytes
  );

  const encryptedBytes = arrayBufferToUint8Array(encryptedBuffer);
  const { ciphertext, authTag } = splitCiphertextAndAuthTag(encryptedBytes);

  return {
    value: uint8ArrayToBase64(ciphertext),
    iv: uint8ArrayToBase64(iv),
    authTag: uint8ArrayToBase64(authTag),
    algo: SENSITIVE_ENTRY_SCHEME,
    keyVersion,
  };
}

export async function decryptSensitiveField({
  value,
  iv,
  authTag,
  key,
  fieldName = 'detail',
  keyVersion = SENSITIVE_ENTRY_KEY_VERSION,
  aad = null,
}) {
  const cryptoObj = getWebCrypto();
  const cryptoKey = await normalizeSensitiveEntryKey(key);

  if (!value) {
    return '';
  }

  if (!iv || !authTag) {
    throw new Error(
      `Missing IV or auth tag for sensitive field "${fieldName}"`
    );
  }

  const ciphertextBytes = base64ToUint8Array(value);
  const ivBytes = base64ToUint8Array(iv);
  const authTagBytes = base64ToUint8Array(authTag);

  if (ivBytes.length !== AES_GCM_IV_LENGTH) {
    throw new Error(
      `Invalid IV length for sensitive field "${fieldName}". Expected ${AES_GCM_IV_LENGTH} bytes`
    );
  }

  if (authTagBytes.length !== AES_GCM_TAG_LENGTH) {
    throw new Error(
      `Invalid auth tag length for sensitive field "${fieldName}". Expected ${AES_GCM_TAG_LENGTH} bytes`
    );
  }

  const combined = combineCiphertextAndAuthTag(ciphertextBytes, authTagBytes);

  const aadValue =
    aad ??
    JSON.stringify({
      scope: 'sensitive-entry',
      fieldName,
      keyVersion,
    });

  const additionalData = buildAadBytes(aadValue);

  const decryptedBuffer = await cryptoObj.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: ivBytes,
      tagLength: AES_GCM_TAG_LENGTH * 8,
      additionalData,
    },
    cryptoKey,
    combined
  );

  return utf8Decode(arrayBufferToUint8Array(decryptedBuffer));
}

export async function encryptSensitiveEntry({
  detail = '',
  detailHtml = '',
  key,
  keyVersion = SENSITIVE_ENTRY_KEY_VERSION,
  aadOverrides = null,
}) {
  const [detailEncrypted, detailHtmlEncrypted] = await Promise.all([
    encryptSensitiveField({
      plaintext: detail,
      key,
      fieldName: 'detail',
      keyVersion,
      aad: aadOverrides?.detail ?? null,
    }),
    encryptSensitiveField({
      plaintext: detailHtml,
      key,
      fieldName: 'detailHtml',
      keyVersion,
      aad: aadOverrides?.detailHtml ?? null,
    }),
  ]);

  return {
    detail: detailEncrypted.value,
    detailIv: detailEncrypted.iv,
    detailAuthTag: detailEncrypted.authTag,
    detailAlgo: detailEncrypted.algo,
    detailKeyVersion: detailEncrypted.keyVersion,

    detailHtml: detailHtmlEncrypted.value,
    detailHtmlIv: detailHtmlEncrypted.iv,
    detailHtmlAuthTag: detailHtmlEncrypted.authTag,
    detailHtmlAlgo: detailHtmlEncrypted.algo,
    detailHtmlKeyVersion: detailHtmlEncrypted.keyVersion,
  };
}

export async function decryptSensitiveEntry({ entry, key }) {
  if (!entry) {
    throw new Error('Entry is required');
  }

  const detail = await decryptSensitiveField({
    value: entry.detail,
    iv: entry.detail_iv ?? entry.detailIv,
    authTag: entry.detail_auth_tag ?? entry.detailAuthTag,
    key,
    fieldName: 'detail',
    keyVersion:
      entry.detail_key_version ??
      entry.detailKeyVersion ??
      SENSITIVE_ENTRY_KEY_VERSION,
  });

  const detailHtml = await decryptSensitiveField({
    value: entry.detail_html ?? entry.detailHtml,
    iv: entry.detail_html_iv ?? entry.detailHtmlIv,
    authTag: entry.detail_html_auth_tag ?? entry.detailHtmlAuthTag,
    key,
    fieldName: 'detailHtml',
    keyVersion:
      entry.detail_html_key_version ??
      entry.detailHtmlKeyVersion ??
      SENSITIVE_ENTRY_KEY_VERSION,
  });

  return {
    ...entry,
    detail,
    detail_html: detailHtml,
    detailHtml,
  };
}

export function isSensitiveEntry(entry) {
  return Boolean(entry?.is_sensitive ?? entry?.isSensitive);
}

export function looksLikeEncryptedSensitiveEntryPayload(entry) {
  if (!entry) return false;

  return Boolean(
    (entry.detail || entry.detail === '') &&
      (entry.detail_iv || entry.detailIv) &&
      (entry.detail_auth_tag || entry.detailAuthTag) &&
      (entry.detail_html || entry.detailHtml || entry.detail_html === '') &&
      (entry.detail_html_iv || entry.detailHtmlIv) &&
      (entry.detail_html_auth_tag || entry.detailHtmlAuthTag)
  );
}

export const sensitiveEntryCrypto = {
  SENSITIVE_ENTRY_ALGO,
  SENSITIVE_ENTRY_KEY_VERSION,
  AES_GCM_IV_LENGTH,
  AES_GCM_TAG_LENGTH,
  generateSensitiveEntryKey,
  exportSensitiveEntryKeyToBase64,
  importSensitiveEntryKey,
  encryptSensitiveField,
  decryptSensitiveField,
  encryptSensitiveEntry,
  decryptSensitiveEntry,
  isSensitiveEntry,
  looksLikeEncryptedSensitiveEntryPayload,
  buildSensitiveEntryAadPreview,
};

export default sensitiveEntryCrypto;
